/** @jsxImportSource @emotion/react */

import { useState } from "react";
import tw from "twin.macro";
import { Card, CardHeader } from "../components/Card";
import { Page } from "../components/Page";

import { CalciteOption } from "@esri/calcite-components-react";
import { Select } from "components/Select";

const versions = {
  "1.1.0": {
    date: "07/15/2025",
    fixes: [
      "Fixed an issue that wasn't allowing any user to access profile page, regardless of if they have dashboard permission",
      "Fixed some layout issues and refined export functionality",
      "Fixed an issue with the MSAG Table being difficult to navigate",
      "Fixed an issue with user profile data not being accurate to the displayed information",
    ],
    features: [
      "Integrated GeneralUse value into ALI List and Detail pages",
      "Enhanced routing specifications based on permission set",
      "Added support for authentication with newer Keycloak versions (26.0.0 and above)",
      "Reworked how the Quick Search tab over quick paste function behaves - now no longer highlights entire input",
    ],
  },
  "1.2.0": {
    date: "09/10/2025",
    fixes: [
      "Fixed an issue with view ALI pin drop showing like instead of exact match which accounted for some far too many results",
      "Fixed a crash with Bulk Change Request page",
      "Fixed an issue with ALI Edit and MSAG Edit not updating responses after an initial error",
      "Fixed an issue with some pages that include Additional Information hotkeys not working",
      "Fixed an issue with ALI Change Requests not including the HNP/HNE in the House number field in current column",
      "Fixed an issue with users with function foresite:discrep:updateByJurisdiction not being able to change to their proper status and update accordingly in Discrepancies",
      "Fixed an issue with discrepancies not updating to no longer have a question",
      "Fixed an issue on Discrepancy table not displaying Unvetted status properly",
      "Removed minimum width for table cells (excluding action cells) to allow for better responsiveness",
    ],
    features: [
      "Implemented Physical OTP Token Server integration allowing for physical tokens to be used for 2FA - reworked users with just Keycloak OTP to be able to use previous method",
      "Reworked the Disability Codes and Private Citizen (foresite:ali:addlData:write) components",
      "Discrepancies that have an open question on the table page are now highlighted in blue",
      "Updated the Discrepancy eye to be an edit page icon instead of view details",
      "Reworked status dropdown in discrepancies to show the status in order of discrepancy lifecycle for both table and details pages",
      "Added Quick filters to Discrepancy table to filter using date",
      "Added Quick Filter to Discrepancy table to filter to only records with open questions",
      "Added Quick Filter to Discrepancy table to filter to only show complete records",
      "Reworked how Discrepancy questions and comments are handled - now they immediately update without needing to hit save",
      "Reworked Discrepancy badge count to be if there is a question or status is Unvetted or Verified",
      "Reworked Discrepancy Detail page to include more proper values and information",
      "In Discrepancy Detail page that has ALI, preserved white space and added quick copy to clipboard functionality",
      "Added a specific view for Discrepancies that are System Generated",
      "Updated the user inactivity timeout to 60 minutes",
      "Renamed Export to Export as Filtered for clarity",
      "Reworked the starting page for users based on their permissions",
      "Added ALI Logs view page for Ali Records",
    ],
  },
  "1.2.1": {
    date: "09/24/2025",
    fixes: [
      "Fixed an issue with ALI Edit page not properly displaying the correct components based on user permissions",
      "Fixed an issue with OTP Code input not displaying on Safari",
    ],
  },
  "1.2.2": {
    date: "09/25/2025",
    fixes: [
      "Fixed an issue with Bulk Change Request values not being properly applied",
      "Fixed an issue with ALI Create page displaying Not Authorized on submit",
    ],
    features: [
      "Reworked how AutoComplete components fetch data to improve performance",
    ],
  },
  "1.2.3": {
    date: "10/14/2025",
    fixes: [
      "Fixed an issue with ELT Records not being filtered properly when exporting",
      "Fixed an issue with Discrepancy Detail page not showing the correct issue",
      "Fixed an issue where adding a Question wouldn't properly save the question",
      "Fixed an issue where a note would not have the timestamp until refresh",
      "Fixed an issue on Safari where filtering on select inputs in tables would not properly update",
      "Fixed an issue with Only Questions and Show Completed filters not saving properly",
    ],
    features: [
      "Added more context to LVF Discrepancy Detail page",
      "Added ability for Bulk Change Requests to save their respective values if their field box is set to MULTIPLE VALUES",
      "Reworked LVF Edit page to allow for multiple fields to be edited at once",
      "Renamed Comments table to Notes table for better clarity",
      "Reworked Show Completed filter to not filter to be completed by default",
      "Reworked ALI Edit save flow to manage if the user made changes to ADDL data or the record, then execute the requests properly based on their permissions",
      "Reworked Discrepancy Badge count to be more accurate to the user permissions",
      "Further reworked how AutoComplete components fetch data to improve performance and selection variety",
      "Reworked how Date filters work to remove time from the filtering (00:00:00 - 23:59:59)",
      "Reworked the default location of New Notes and New Question popup window",
    ],
  },
  "1.3.0": {
    date: "10/31/2025",
    fixes: [
      "Fixed an issue where a race condition caused some login flows to be wrong",
      "Fixed an with login screen where the show password button was bigger than the input field",
      "Fixed some layout issues when using the application on larger screens",
    ],
    features: [
      "Reworked discrepancy detail headers to more clearly show the discrepancy type and relevancy",
      "Added New Telephone on dashboard functionality",
      "Added Statistical Reports to the discrepancy page",
      "Add a shortcut when trying to add a new telephone number that already exists to edit that record or migrate the CPN",
      "Added functionality for all Dashboard elements",
      "Reworked ALI record edits FOC select components",
    ],
  },
  "1.3.1": {
    date: "11/05/2025",
    fixes: [
      "Fixed an issue where the CPF would be displayed incorrectly on ALI Records and ALI Errors",
      "Fixed an issue where FOC current value wouldn't show full migrate label when assigned",
      "Fixed an issue where the NENA ID component wasn't read only and displayed whichever current CPF value was first retrieved in ALI Edits and ALI Error Edits",
      "Fixed an issue on ALI Create where if there were multiple MSAG records returned, it would return Not Authorized",
      "Fixed an issue where on Add Telephone dashboard shortcut would not properly pass CPN state to ALI Create page",
    ],
    features: [
      "Clicking on the logo will now default to navigating to the first available navigation link",
      "On ALI record create, when done filling out all the autocompletes, under House Number it will display the available low and high ranges for the fetched MSAG records",
      "Added a display for active filters on all table pages",
    ],
  },
  "1.3.2": {
    date: "12/05/2025",
    fixes: [
      "Reworked implementation of some requests that allowed for SQL Injection vulnerabilities",
    ],
  },
  "1.4.0": {
    date: "12/18/2025",
    fixes: [
      "Fixed display of House Number on ALI Log page",
      "Fixed an issue with Export County ID having a max characters of 3 instead of 4",
      "Fixed an issue with Discrepancies values (i.e. timestamp) being changed on status update",
      "Fixed an issue with sorting EMS2 on ELT Tables",
      "Fixed an issue with NENAv2.1 export on ALI Records using the wrong ESN value",
    ],
    features: [
      "On Discrepancy page, now you can clear all additional filters with a Clear button",
      "Cleared up FOC display on ALI Edit by removing Insert from edit page",
    ],
  },
  "1.4.1": {
    date: "12/19/2026",
    fixes: ["Fixed an issue with discreps EDIT data being saved incorrectly"],
  },
  "1.5.0": {
    date: "03/18/2026",
    fixes: [
      "Fixed an issue with Autocompletes wiping Prefix and Postfix",
      "Fixed an issue where 'Please Pick a Value from this Field' was being displayed during inproper times",
      "Fixed issues where inputs on ALI Edit pages could be set to values that are bigger then the max character limit which caused issues with saving records",
      "Fixed an issue where Notes added on ALI Edit Discrepancy Create would not be added to the Discrepancy, forcing the user to use Bulk Change Request instead",
      "Fixed an issue where Private Citizen filter was being misleading when filtering",
      "Fixed an issue where on Discrepancy Detail page, if the user didn't have permissions to set specific statuses, the wrong status would be displayed",
    ],
    features: [
      "On each table, the displayed record count will now be saved respective to each table across pages",
      "House Number inputs will now display ranges when the State, MCN, and Street name filled out, this can be further narrowed down with Street Suffix",
      "House Number inputs will only display when the user is focused on the input",
      "House Number displays under the input are enhanced to show the Low and High range with Side displays",
      "House Number inputs also allow user to press up and down arrows to navigate through the available ranges when focused on the input",
      "Reworked the ALI Migrate page to be more akin to the ALI Edit page and allow for more information to be displayed and edited during a migration, this is reflected on the Quick Start Dashboard shortcut as well",
      "Added Discrepancy Export as Filter functionality",
      "When creating a discrepancy, either through ALI Edit or Bulk Change Request, the popup window will display the ID/IDs of the created discrepancies",
      "Autocomplete inputs allow the user to press tab if there is only one option to select that option",
      "Autocomplete inputs will now allow for case insensitive searching and will automatically input the correct case of the option when selected",
      "Autocomplete inputs will now allow for autofills if there is only one MSAG record available on Create or Migrate ALI",
      "On Discrepancy Detail page, the dtype will always be displayed",
      "On ALI Edit, the default FOC is now Change instead of Unlock",
      "Added Last Modified to be filterable on all tables that previously didn't have it as a filter option",
    ],
  },
  "1.5.1": {
    date: "03/19/2026",
    fixes: [
      "Fixed an issue where records were not returning all their values on searches",
    ],
  },
  "1.5.2": {
    date: "03/20/2026",
    fixes: [
      "Fixed an issue where requests not matching with case sensitive within Criteria it would not save within the database",
    ],
  },
  "1.5.3": {
    date: "03/20/2026",
    fixes: [
      "Fixed an issue when editing ALI Records where discrepancies would be created even if no fields were editing for them due to null versus empty string discrepancy field values",
    ],
  },
  "1.5.4": {
    date: "06/29/2026",
    fixes: [
      "Fixed a a crash on users having MSAG Change and MSAG view permissions",
      "Fixed an issue with using arrow keys and enter key to navigate certain filter boxes",
      "Fixed an issue with Discrepancies Export as Filter not properly applying all filters to the export (date, show completed, only questions)",
      "Fixed an issue with some table rows not being properly displayed based on null values in certain fields",
      "Fixed an issue with ALI Record tables having slow load times",
      "Fixed an issue on Bulk Change Request where if the user had no notes, a blank note would be created",
    ],
    features: [
      "Added Excel format to Statistical Report exports on the Discrepancy page",
      "Added a quick export autopopulate for Statistical Reports based on the currently applied filters",
      "Added an autopopulate for New ALI Records for Main NPA and Main Number based on the new telephone number",
      "Added sticky headers to all tables within the application for better navigation and context when scrolling through records",
      "Increased max notes length to 2000 characters",
      "Highlights on ALI Change Request Table rows are now for any difference between on the original, requested, and current values instead of just original/current versus requested",
      "Reworked some table headers on Discrepancy page to be more clear and concise",
      "Reworked some export headers on Discrepancy page to be more clear and concise",
    ],
  },
};

const VersionHistory = () => {
  const latestVersions = Object.keys(versions).sort().reverse();
  const [version, setVersion] = useState(latestVersions[0]);

  return (
    <>
      <Page title="Version History" label="Version History">
        <Card css={[tw`drop-shadow-sm rounded-md border border-gray-200`]}>
          <CardHeader>
            <div css={tw`flex items-center justify-between w-full`}>
              <Select
                label="Version"
                value={version}
                name="version"
                onChange={(e) => setVersion(e.target.value)}
                scale="m"
                labelSize="m"
              >
                {Object.keys(versions).map((option) => (
                  <CalciteOption key={option} value={option}>
                    {option}
                  </CalciteOption>
                ))}
              </Select>
              <div css={tw`ml-4 flex items-center`}>
                <span css={tw`font-semibold mr-2`}>Release Date:</span>
                {versions[version].date}
              </div>
            </div>
          </CardHeader>

          <div css={[tw`grid gap-y-4 w-full`]}>
            <div>
              {versions[version].features && (
                <>
                  <div css={tw`font-semibold mb-1`}>Features</div>
                  <ul css={tw`list-disc pl-8`}>
                    {versions[version].features.map((feature, idx) => (
                      <li key={idx} css={tw`ml-0`}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {versions[version].fixes && (
                <>
                  <div css={tw`font-semibold mt-4 mb-1`}>Fixes</div>
                  <ul css={tw`list-disc pl-8`}>
                    {versions[version].fixes.map((fix, idx) => (
                      <li key={idx} css={tw`ml-0`}>
                        {fix}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </Card>
      </Page>
    </>
  );
};

export default VersionHistory;
